/**
 * Appearance Page
 *
 * Theme settings for dark/light/system mode.
 * Persists to backend via updateUserSettings mutation
 * and syncs across all Bloomcode applications.
 */

import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  cn,
} from '@bloomcode/ui';
import { MetaTags } from '@bloomcode/shared-modules';
import { useTheme } from '@bloomcode/theme';
import { useUpdateUserSettingsMutation } from '@bloomcode/graphql';
import { Sun, Moon, Monitor, Check, Palette } from '@bloomcode/icons';

type ThemeOption = 'light' | 'dark' | 'system';

const themeOptions: {
  value: ThemeOption;
  label: string;
  description: string;
  icon: typeof Sun;
}[] = [
  {
    value: 'light',
    label: 'Light',
    description: 'Clean and bright interface',
    icon: Sun,
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Easy on the eyes in low light',
    icon: Moon,
  },
  {
    value: 'system',
    label: 'System',
    description: 'Follows your device settings',
    icon: Monitor,
  },
];

export default function AppearancePage() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [updateUserSettings] = useUpdateUserSettingsMutation();

  const handleThemeChange = async (newTheme: ThemeOption) => {
    setTheme(newTheme);
    setSaving(true);
    setSaved(false);

    try {
      await updateUserSettings({
        variables: {
          input: {
            themePreference: newTheme.toUpperCase(),
          },
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // Theme is already applied locally via ThemeProvider
      // API sync failure is non-blocking
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Appearance - Bloomcode Account</title>
      </Helmet>
      <MetaTags
        title="Appearance Settings"
        description="Customize how Bloomcode looks across all your applications"
      />
      <div className="container mx-auto px-4 py-8 space-y-6">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Appearance</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div>
          <h1 className="text-2xl font-bold font-heading">Appearance</h1>
          <p className="text-muted-foreground mt-1">
            Customize how Bloomcode looks across all your applications.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-primary" />
              Theme
            </CardTitle>
            <CardDescription>
              Choose your preferred color mode. This setting applies to all Bloomcode applications.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Label className="text-sm font-medium mb-3 block">Color Mode</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {themeOptions.map((option) => {
                const isSelected = theme === option.value;
                const Icon = option.icon;

                return (
                  <button
                    key={option.value}
                    onClick={() => handleThemeChange(option.value)}
                    disabled={saving}
                    className={cn(
                      'relative flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200',
                      isSelected
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    )}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <Check className="h-4 w-4 text-primary" />
                      </div>
                    )}

                    {/* Preview — uses theme-aware classes for the preview boxes */}
                    <div
                      className={cn(
                        'w-full aspect-video rounded-md border overflow-hidden',
                        option.value === 'dark'
                          ? 'bg-foreground/95 border-foreground/20'
                          : option.value === 'light'
                            ? 'bg-background border-border'
                            : 'bg-gradient-to-r from-background to-foreground/95 border-border'
                      )}
                    >
                      <div className="p-2 space-y-1.5">
                        <div
                          className={cn(
                            'h-1.5 w-8 rounded-full',
                            option.value === 'dark' ? 'bg-background/90' : 'bg-foreground/90'
                          )}
                        />
                        <div
                          className={cn(
                            'h-1 w-12 rounded-full opacity-40',
                            option.value === 'dark' ? 'bg-background/90' : 'bg-foreground/90'
                          )}
                        />
                        <div
                          className={cn(
                            'h-3 w-full rounded opacity-10',
                            option.value === 'dark' ? 'bg-background/90' : 'bg-foreground/90'
                          )}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{option.label}</span>
                    </div>
                    <span className="text-xs text-muted-foreground text-center">
                      {option.description}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Status */}
            <div className="mt-4 flex items-center gap-2 text-sm">
              {saving && <span className="text-muted-foreground">Saving...</span>}
              {saved && (
                <span className="text-primary flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" />
                  Saved across all apps
                </span>
              )}
              {!saving && !saved && (
                <span className="text-muted-foreground">
                  Currently using: {resolvedTheme === 'dark' ? 'Dark' : 'Light'} mode
                  {theme === 'system' && ' (system)'}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
